import React, { ComponentType, FC, ReactNode, useCallback } from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import { ConventionSetting } from '../../shared/config';
import { PermissionName } from '../../shared/permissions';
import { useTranslation } from '../translations';
import { checkPermission, useConfig, useConvention, useFetcher, useQuery, useUser } from '../utils';
import { AccessDenied } from '.';

interface UserStateProps {
  readonly children?: ReactNode;
  readonly requireLoggedOut?: boolean;
}

interface UserRouteProps {
  readonly requirePermissions?: PermissionName[] | PermissionName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly component?: ComponentType<any>;
  readonly render?: () => ReactNode;
  readonly path?: string | readonly string[] | undefined;
  readonly exact?: boolean | undefined;
  readonly noRedirect?: boolean;
  readonly matchAny?: boolean;
}

export const UserRoute: FC<UserRouteProps> = ({
  requirePermissions,
  component,
  exact,
  path,
  render,
  noRedirect,
  matchAny,
}) => {
  const user = useUser();

  if (!user) {
    return <LoginRedirect noRedirect={noRedirect} />;
  }

  if (requirePermissions && !checkPermission(user, requirePermissions, matchAny)) {
    return (
      <Container className="justify-content-center">
        <AccessDenied />
      </Container>
    );
  }

  return <Route component={component} exact={exact} path={path} render={render} />;
};

export const UserStateComponent: FC<UserStateProps> = ({ children, requireLoggedOut }) => {
  const user = useUser();
  if (requireLoggedOut && user) {
    return <RedirectAfterLogin />;
  }

  return (
    <Container className="justify-content-center">
      <Row>
        <Col xs={12}>{children}</Col>
      </Row>
    </Container>
  );
};

export const RedirectAfterLogin: FC = () => {
  const config = useConfig();
  const params = useQuery();
  const redirect = params.get('redirect');
  const extParams = params.get('params') ?? '';
  const conventionId = Number.parseInt(params.get('conventionId') ?? '', 10);
  const path = redirect ? `${redirect}${decodeURIComponent(extParams)}` : '/';

  for (const c of config.conventions) {
    if (conventionId === c.id) {
      return <ConventionRedirect convention={c} extParams={extParams} redirect={redirect} />;
    }
  }

  // For redirect scenarios where the user is going to be redirected to an external website
  if (path.includes('api/')) {
    window.location.href = path;
    return null;
  }

  return <Redirect to={path} />;
};

interface ConventionRedirectProps {
  readonly convention: ConventionSetting;
  readonly redirect: string | null;
  readonly extParams: string;
}

export const ConventionRedirect: FC<ConventionRedirectProps> = ({
  convention,
  redirect,
  extParams,
}) => {
  const redirectRequest = useFetcher(async () => {
    return await api.getRedirectRequest(convention.id);
  }, [convention.id]);

  const { ts } = useTranslation();

  const user = useUser();

  if (redirectRequest.loading) {
    return (
      <>
        {ts('redirecting_user_to')}
        {convention.longName}
      </>
    );
  }

  if (redirectRequest.error) {
    return ts('error_while_redirecting_user_please_contact_support');
  }

  if (!redirectRequest.data.token) {
    return ts('error_while_redirecting_user_please_try_again');
  }

  return (
    <HiddenRedirectForm
      host={convention.domain}
      params={extParams}
      redirect={redirect ?? '/'}
      token={redirectRequest.data.token}
      userId={user!.id}
    />
  );
};

interface HiddenRedirectFormProps {
  readonly host: string;
  readonly token: string;
  readonly userId: number;
  readonly redirect: string;
  readonly params: string;
}

const HiddenRedirectForm: FC<HiddenRedirectFormProps> = ({
  host,
  token,
  userId,
  params,
  redirect,
}) => {
  const url = `${location.protocol}//${host}/api/login/from_redirect`;
  if (params !== '') {
    redirect += params;
  }

  const submit = useCallback((form: HTMLFormElement | null) => {
    form?.submit();
  }, []);

  return (
    <form action={url} method="post" ref={submit}>
      <input name="token" type="hidden" value={token} />
      <input name="userId" type="hidden" value={userId.toString()} />
      <input name="redirect" type="hidden" value={redirect} />
    </form>
  );
};

export const LoginRedirect: FC<{ readonly noRedirect?: boolean }> = ({ noRedirect }) => {
  const { conventions, activeConventionId } = useConfig();
  const location = useLocation();
  const convention = useConvention();
  const query = useQuery();

  const { domain } = conventions.find((t) => t.id === activeConventionId)!;
  const params = location.search.length > 0 ? `&params=${encodeURIComponent(location.search)}` : '';
  const redirect = noRedirect ? '/' : `${location.pathname}${params}`;
  let conventionId = convention.id === activeConventionId ? '' : `&conventionId=${convention.id}`;

  const queryConventionId = query.get('conventionId');
  if (!conventionId && queryConventionId) {
    conventionId = `&conventionId=${queryConventionId}`;
  }

  window.location.replace(
    `${window.location.protocol}//${domain}/login?redirect=${redirect}${conventionId}`,
  );

  return null;
};
